import React, { useState, useEffect } from 'react';
import { formatMontant, nettoyerLibelle, getDeviseCourante } from '../../utils/format';
import { Plus, Trash2, Search } from 'lucide-react';
import api from '../../services/api';
import { Button, Input } from '../../components/common/Input';
import InputMontant from '../../components/common/InputMontant';
import toast from 'react-hot-toast';

export default function FormSortieMatin({ vendeur, onSuccess, onCancel }) {
  const [date] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [lignes, setLignes] = useState([]);
  const [search, setSearch] = useState('');
  const [produits, setProduits] = useState([]);
  const [cartons, setCartons] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);

  // Boutique d'où sort la marchandise confiée au vendeur
  const [boutiques, setBoutiques] = useState([]);
  const [boutiqueId, setBoutiqueId] = useState('');

  useEffect(() => {
    api.get('/produits', { params: { disponible: 'true', limit: 200 } })
      .then(r => setProduits(r.data.produits || []))
      .catch(() => {});
    api.get('/cartons').then(r => setCartons(r.data || [])).catch(() => {});
    api.get('/boutiques')
      .then(r => {
        const actives = (r.data || []).filter(b => b.statut === 'actif');
        setBoutiques(actives);
        const principale = actives.find(b => b.nom === 'Principale') || actives[0];
        if (principale) setBoutiqueId(principale.id);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!search.trim()) { setSearchResults([]); return; }
    const q = search.toLowerCase();
    const results = [];
    // Conditionnements (lots) d'abord
    cartons.forEach(c => {
      if (c.nom?.toLowerCase().includes(q) || c.code?.toLowerCase().includes(q)) {
        results.push({ carton: c });
      }
    });
    produits.forEach(p => {
      (p.variantes || []).filter(v => v.actif && v.stock_disponible > 0).forEach(v => {
        // Nom + caractéristiques lisibles ; le code interne (aléatoire) seulement
        // pour une recherche précise (≥ 4 caractères) → évite les résultats sans rapport.
        if (p.nom.toLowerCase().includes(q) || v.modele?.toLowerCase().includes(q) ||
            v.couleur?.toLowerCase().includes(q) || v.taille_nom?.toLowerCase().includes(q) ||
            (q.length >= 4 && v.code_variante?.toLowerCase().includes(q))) {
          results.push({ produit: p, variante: v });
        }
      });
    });
    setSearchResults(results.slice(0, 10));
  }, [search, produits, cartons]);

  const ajouterLigne = (produit, variante) => {
    if (lignes.find(l => l.variante_id === variante.id)) {
      toast('Déjà ajouté', { icon: '⚠️' }); return;
    }
    // Nouvel article ajouté EN TÊTE → le dernier confié reste toujours en haut.
    setLignes(prev => [{
      variante_id: variante.id,
      produit_nom: produit.nom,
      modele: variante.modele,
      taille: variante.taille_nom || '',
      couleur: variante.couleur,
      code_variante: variante.code_variante,
      stock_disponible: variante.stock_disponible,
      quantite: 1,
      prix_vente_unitaire: variante.prix_vente_magasin || 0,
    }, ...prev]);
    setSearch(''); setSearchResults([]);
  };

  // Ajoute un conditionnement (lot). Il sera éclaté en articles à la sortie
  // (prix dérivé de chaque article) ; ici on saisit juste le nombre de lots.
  const ajouterCarton = (carton) => {
    if (lignes.find(l => l.carton_id === carton.id)) {
      toast('Déjà ajouté', { icon: '⚠️' }); return;
    }
    // Valeur d'UN lot = somme des prix de vente normaux de ses articles
    // (c'est ce que le vendeur devra justifier, le lot étant éclaté à la sortie).
    const valeurLot = (carton.composants || []).reduce(
      (s, c) => s + Number(c.quantite) * (Number(c.prix_vente_magasin) || 0), 0
    );
    setLignes(prev => [{
      carton_id: carton.id,
      produit_nom: carton.nom,
      contenu: (carton.composants || []).map(c => `${c.quantite}× ${c.produit_nom}`).join(' + '),
      formable: carton.stock_disponible,
      valeur_lot: valeurLot,
      quantite: 1,
    }, ...prev]);
    setSearch(''); setSearchResults([]);
  };

  const mettreAJour = (i, field, value) => {
    setLignes(prev => prev.map((l, idx) => idx === i ? { ...l, [field]: value } : l));
  };

  const supprimer = (i) => setLignes(prev => prev.filter((_, idx) => idx !== i));

  const totalPieces = lignes.reduce((s, l) => s + (parseInt(l.quantite) || 0), 0);
  // Montant attendu : articles à l'unité (qté × prix) + lots (nb lots × valeur
  // du lot = somme des prix de vente des articles qui le composent).
  const montantAttendu = lignes.reduce((s, l) => s + (
    l.carton_id
      ? (parseInt(l.quantite) || 0) * (Number(l.valeur_lot) || 0)
      : (parseInt(l.quantite) || 0) * (parseInt(l.prix_vente_unitaire) || 0)
  ), 0);

  const handleSubmit = async (confirmerPerte = false) => {
    if (lignes.length === 0) return toast.error('Ajoutez au moins un article');
    for (const l of lignes) {
      if (!l.quantite || l.quantite <= 0) return toast.error(`Quantité invalide pour ${l.produit_nom}`);
      if (l.carton_id) continue; // lot : stock + prix vérifiés côté serveur (par composant)
      if (l.quantite > l.stock_disponible) return toast.error(`Stock insuffisant pour ${l.produit_nom} (dispo: ${l.stock_disponible})`);
      if (!l.prix_vente_unitaire || l.prix_vente_unitaire <= 0) return toast.error(`Prix manquant pour ${l.produit_nom}`);
    }
    if (boutiques.length > 1 && !boutiqueId) {
      return toast.error('Choisissez la boutique d\'où sort la marchandise');
    }
    setLoading(true);
    try {
      await api.post(`/vendeurs/${vendeur.id}/sorties`, {
        date, notes,
        boutique_id: boutiqueId || undefined,
        confirmer_vente_perte: confirmerPerte,
        lignes: lignes.map(l => (
          l.carton_id
            ? { carton_id: l.carton_id, quantite: parseInt(l.quantite) }
            : { variante_id: l.variante_id, quantite: parseInt(l.quantite), prix_vente_unitaire: parseInt(l.prix_vente_unitaire) }
        )),
      });
      onSuccess();
    } catch (err) {
      // Garde-fou rentabilité : prix de sortie sous le coût → confirmation.
      if (err.response?.status === 409 && err.response.data?.code === 'VENTE_A_PERTE' && !confirmerPerte) {
        const recap = (err.response.data.lignes || [])
          .map(l => `• ${l.libelle || 'article'} : ${Number(l.prix).toLocaleString()} < coût ${Number(l.cout).toLocaleString()} ${getDeviseCourante()}`)
          .join('\n');
        if (window.confirm(`⚠️ Prix de sortie sous le coût\n\n${recap}\n\nConfier au vendeur à ce prix ?`)) {
          return handleSubmit(true);
        }
        return;
      }
      toast.error(err.response?.data?.message || 'Erreur serveur');
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-5">
      {/* Info vendeur */}
      <div className="bg-yellow-600/10 border border-yellow-600/30 rounded-xl p-4 flex items-center justify-between">
        <div>
          <p className="text-yellow-400 font-semibold">{vendeur.prenom} {vendeur.nom}</p>
          <p className="text-gray-400 text-sm">{date}</p>
        </div>
        <div className="text-right text-sm">
          <p className="text-gray-400">{totalPieces} pièce(s)</p>
          <p className="text-yellow-400 font-bold">{formatMontant(montantAttendu)}</p>
        </div>
      </div>

      {/* Boutique source — visible si plusieurs boutiques */}
      {boutiques.length > 1 && (
        <div className="bg-yellow-500/10 border border-yellow-500/40 rounded-xl p-3">
          <label className="block text-sm font-semibold text-yellow-400 mb-1.5">
            Boutique d'où sort la marchandise *
          </label>
          <select value={boutiqueId} onChange={e => setBoutiqueId(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-yellow-500">
            <option value="">— Choisir la boutique —</option>
            {boutiques.map(b => (
              <option key={b.id} value={b.id}>{b.nom}{b.ville ? ` — ${b.ville}` : ''}</option>
            ))}
          </select>
          <p className="text-[11px] text-gray-400 mt-1">Le stock confié au vendeur est retiré de cette boutique.</p>
        </div>
      )}

      {/* Recherche */}
      <div>
        <label className="block text-sm text-gray-400 mb-1.5">Ajouter un article (stock disponible uniquement)</label>
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher — nom, modèle, couleur, code..."
            className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:border-yellow-500" />
          {searchResults.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-gray-800 border border-gray-700 rounded-xl shadow-2xl z-20 max-h-64 overflow-y-auto">
              {searchResults.map((r) => r.carton ? (
                <button key={`c-${r.carton.id}`} onClick={() => ajouterCarton(r.carton)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-700 text-left border-l-2 border-yellow-500/60">
                  <div className="min-w-0">
                    <p className="text-white text-sm font-medium">{r.carton.nom} <span className="text-yellow-400/80 text-[11px]">conditionnement</span></p>
                    <p className="text-gray-400 text-xs truncate">{(r.carton.composants || []).map(c => `${c.quantite}× ${c.produit_nom}`).join(' + ')}</p>
                  </div>
                  <span className="text-green-400 text-xs ml-3 flex-shrink-0">{r.carton.stock_disponible} formable(s)</span>
                </button>
              ) : (
                <button key={r.variante.id} onClick={() => ajouterLigne(r.produit, r.variante)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-700 text-left">
                  <div>
                    <p className="text-white text-sm font-medium">{r.produit.nom}</p>
                    <p className="text-gray-400 text-xs">{nettoyerLibelle(r.variante.modele)} — {r.variante.taille_nom || ''} {r.variante.couleur} · {r.variante.code_variante}</p>
                  </div>
                  <span className="text-green-400 text-xs ml-3 flex-shrink-0">{r.variante.stock_disponible} dispo</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Articles */}
      {lignes.length > 0 ? (
        <div className="space-y-2">
          {lignes.map((l, i) => (
            <div key={i} className="bg-gray-800 rounded-xl p-3">
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">
                    {l.carton_id ? `${l.produit_nom}` : l.produit_nom}
                    {l.carton_id && <span className="text-yellow-400/80 text-[11px] ml-1">lot</span>}
                  </p>
                  {l.carton_id ? (
                    <>
                      <p className="text-gray-500 text-xs truncate">{l.contenu}</p>
                      <p className="text-gray-600 text-xs">Éclaté en articles à la sortie · {l.formable} lot(s) formable(s)</p>
                    </>
                  ) : (
                    <>
                      <p className="text-gray-500 text-xs">{l.modele} {l.taille ? `· ${l.taille}` : ''} · {l.couleur}</p>
                      <p className="text-gray-600 text-xs font-mono">{l.code_variante} · Stock: {l.stock_disponible}</p>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="space-y-1">
                    <label className="text-xs text-gray-500">{l.carton_id ? 'Nb lots' : 'Qté'}</label>
                    <input type="number" min="1" max={l.carton_id ? undefined : l.stock_disponible}
                      value={l.quantite || ''}
                      onChange={e => mettreAJour(i, 'quantite', e.target.value === '' ? 0 : parseInt(e.target.value) || 0)}
                      placeholder="0"
                      className="w-16 bg-gray-700 border border-gray-600 text-white rounded-lg px-2 py-1.5 text-sm text-center focus:outline-none focus:border-yellow-500" />
                  </div>
                  {l.carton_id ? (
                    <div className="space-y-1 text-right w-28">
                      <p className="text-xs text-gray-500">Valeur / lot</p>
                      <p className="text-white text-sm">{formatMontant(l.valeur_lot || 0)}</p>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <label className="text-xs text-gray-500">Prix vente ({getDeviseCourante()})</label>
                      <InputMontant
                        value={l.prix_vente_unitaire}
                        onChange={v => mettreAJour(i, 'prix_vente_unitaire', v)}
                        placeholder="0"
                        className="w-28 bg-gray-700 border border-gray-600 text-white rounded-lg px-2 py-1.5 text-sm text-center focus:outline-none focus:border-yellow-500" />
                    </div>
                  )}
                  <div className="space-y-1 text-right">
                    <p className="text-xs text-gray-500">Sous-total</p>
                    <p className="text-yellow-400 text-sm font-bold">
                      {l.carton_id
                        ? formatMontant((parseInt(l.quantite) || 0) * (Number(l.valeur_lot) || 0))
                        : formatMontant((parseInt(l.quantite) || 0) * (parseInt(l.prix_vente_unitaire) || 0))}
                    </p>
                  </div>
                  <button onClick={() => supprimer(i)} className="p-1.5 text-red-400 hover:bg-red-900/30 rounded-lg">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}

          {/* Total */}
          <div className="flex items-center justify-between bg-yellow-600/10 border border-yellow-600/30 rounded-xl px-4 py-3">
            <div>
              <p className="text-gray-300 text-sm font-medium">{totalPieces} pièce(s) confiée(s)</p>
              <p className="text-gray-500 text-xs">à remettre ou retourner ce soir</p>
            </div>
            <div className="text-right">
              <p className="text-gray-400 text-xs">Montant attendu</p>
              <p className="text-yellow-400 font-bold text-lg">{formatMontant(montantAttendu)}</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="border-2 border-dashed border-gray-700 rounded-xl p-8 text-center">
          <p className="text-gray-500 text-sm">Recherchez des articles ci-dessus pour les ajouter</p>
        </div>
      )}

      <Input label="Notes (optionnel)" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Instructions particulières..." />

      <div className="flex space-x-3 pt-2">
        <Button type="button" variant="secondary" onClick={onCancel} className="flex-1">Annuler</Button>
        <Button loading={loading} onClick={() => handleSubmit()} className="flex-1" disabled={lignes.length === 0}>
          Enregistrer la sortie ({lignes.length} article{lignes.length > 1 ? 's' : ''})
        </Button>
      </div>
    </div>
  );
}
